import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icons,
  iconSize,
  Link,
  TextField,
  Tooltip,
  Typography,
  useSnackbar,
  visuallyHidden,
} from '@munifocus/ui';
import { SAFETY_CONFIG, type SafetyStatus } from '@munifocus/contracts-gov';
import { newClientId, shortTime, useRaiseSos, useSafetyStatus } from '../../lib/safety-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { HoldButton } from './HoldButton';

/**
 * The officer's SOS, in the shell on every screen — only where a municipality
 * they work at has officer safety on.
 *
 * Two deliberate steps so it never fires by accident: hold for two seconds,
 * then confirm. The idempotency key is minted when the confirm dialog opens,
 * so "Send" pressed twice, or a retry after a dropped connection, is one alarm.
 * If it cannot be sent, the dialog says so and offers 911 — the portal is
 * never the only way to call for help.
 */
export function SosControl({ onDark = false }: { onDark?: boolean }) {
  const status = useSafetyStatus();
  if (!status.data?.enabled) return null;
  return <SosControlInner status={status.data} onDark={onDark} />;
}

function SosControlInner({ status, onDark }: { status: SafetyStatus; onDark: boolean }) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const raise = useRaiseSos();
  const { id: routeFileId } = useParams();
  const hintId = useId();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const clientId = useRef<string>('');

  const openSos = status.openSos;
  const acked = openSos.find((e) => e.status === 'acknowledged');
  // The file on screen, if any, so supervisors know where — the API checks it.
  const fileId = status.timer?.fileId ?? routeFileId ?? null;

  const begin = () => {
    clientId.current = newClientId();
    raise.reset();
    setNote('');
    setOpen(true);
  };

  const send = () => {
    raise.mutate(
      { clientId: clientId.current, fileId, note: note.trim() || null },
      {
        onSuccess: () => {
          snackbar.success(t('gov.safety.sos.sent'));
          setOpen(false);
        },
      },
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {openSos.length > 0 && (
          <Typography
            role="status"
            sx={{
              display: { xs: 'none', lg: 'block' },
              fontSize: 12,
              fontWeight: 600,
              color: onDark ? 'inherit' : 'error.main',
              whiteSpace: 'nowrap',
            }}
          >
            {acked
              ? t('gov.safety.sos.acknowledged', { name: acked.acknowledgedByName ?? '' })
              : t('gov.safety.sos.open', { time: shortTime(openSos[0].raisedAt, i18n.language) })}
          </Typography>
        )}
        <Tooltip title={t('gov.safety.sos.hold')}>
          <span>
            <HoldButton
              holdMs={SAFETY_CONFIG.sosHoldMs}
              onComplete={begin}
              holdingLabel={t('gov.safety.sos.holding')}
              describedBy={hintId}
              variant="contained"
              color="error"
              size="small"
              startIcon={<Icons.Siren size={iconSize.sm} aria-hidden />}
              data-testid="sos-hold"
              sx={{ minWidth: 0, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {t('gov.safety.sos.button')}
            </HoldButton>
          </span>
        </Tooltip>
        <Box component="span" id={hintId} sx={visuallyHidden}>
          {t('gov.safety.sos.hold')}
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => !raise.isPending && setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('gov.safety.sos.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('gov.safety.sos.confirmBody')}</DialogContentText>
          <TextField
            label={t('gov.safety.sos.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          {raise.isError && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              {apiErrorMessage(raise.error, t('gov.safety.sos.failed'))}{' '}
              <Link href="tel:911" sx={{ fontWeight: 700 }}>
                {t('gov.safety.sos.call911')}
              </Link>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={raise.isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={send} disabled={raise.isPending}>
            {raise.isPending ? t('gov.safety.sos.sending') : t('gov.safety.sos.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
